import React, { useContext } from 'react';
import * as yup from 'yup';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import SvgMoney from '@components/ringil3/icons/Money.tsx';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import PriceTag from '@components/RingilComponents/tags/priceTag/PriceTag.tsx';
import InputPriceWithCurrency from '@components/ringil3/Input/InputPriceWithCurrency/InputPriceWithCurrency.tsx';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import { ApiContext } from '@api/api.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { PriceDTO } from '@api/ringil-api.v2.tsx';

interface ComponentProps {
  priceDto?: PriceDTO;
  showPrices?: boolean;
  readOnly: boolean;
  // required only when the tag is editable (readOnly === false)
  expeditionId?: string;
  onReload?: () => void;
  dataTestId?: string;
}

interface Form {
  price: number;
  currency: string;
}

const ArrangedPriceTag: React.FC<ComponentProps> = ({ priceDto, readOnly, showPrices = true, expeditionId, onReload, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const { changeFinalPrice } = useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const save = (data: Form) => {
    changeFinalPrice(expeditionId, { price: data.price, currency: data.currency }, () => {
      if (onReload) onReload();
    });
  };

  const form = useForm<Form>(
    yup.object().shape({
      price: yup.number().required(),
      currency: yup.string().required(),
    }),
    { price: priceDto?.price, currency: priceDto?.currency },
    d => save(d),
    false,
    false,
    null,
    'none',
  );

  return (
    <>
      {isNotNullOrUndefined(priceDto) && (
        <ButtonTag
          label={tr(`ArrangedPriceTag.title`, `Arranged price`)}
          value={showPrices ? <PriceTag priceDto={priceDto} badgeType={'none'} /> : '-'}
          icon={<SvgMoney />}
          actionIcon={readOnly ? undefined : <SvgTagEdit />}
          readonly={readOnly}
          dataTestId={dataTestId}
          onSave={readOnly ? undefined : (onSuccess) => {
            form.validateAndSend();
            onSuccess();
          }}
          autoClose
        >
          {!readOnly && (
            <VerticalFieldAndValue label={tr(`ArrangedPriceTag.price`, `Price`)} required errorMessage={form.errors.price}>
              <InputPriceWithCurrency
                value={form.values.price}
                currency={form.values.currency}
                changePrice={price => form.setFieldValue(form.names.price, price)}
                changeCurrency={currency => form.setFieldValue(form.names.currency, currency)}
                companyId={loggedUser.companyId}
              />
            </VerticalFieldAndValue>
          )}
        </ButtonTag>
      )}
    </>
  );
};

export default ArrangedPriceTag;
